export const taskSources = ['work', 'actions', 'mail', 'reports', 'onboarding', 'questions'] as const;
export const taskDueStates = ['overdue', 'today', 'upcoming', 'undated'] as const;
export const taskSearchLimit = 120;
export type TaskFilters = { source: string; dueState: string; sort: 'due' | 'title'; page: number };
export type TaskReturnContext = { to: string; search: string; focusKey: string };

/** Only non-sensitive queue controls belong in a shareable URL. */
export function readTaskFilters(search: string): TaskFilters {
  const params = new URLSearchParams(search);
  const source = params.get('source') ?? '';
  const dueState = params.get('dueState') ?? '';
  const page = Number(params.get('page') ?? 1);
  return {
    source: taskSources.some(value => value === source) ? source : '',
    dueState: taskDueStates.some(value => value === dueState) ? dueState : '',
    sort: params.get('sort') === 'title' ? 'title' : 'due',
    page: Number.isSafeInteger(page) && page > 0 && page <= 20001 ? page : 1,
  };
}

export function taskQueueHref(filters: TaskFilters, building?: string, root: '/work' | '/my-work' = '/work'): string {
  const params = new URLSearchParams();
  if (building) params.set('building', building);
  if (filters.source) params.set('source', filters.source);
  if (filters.dueState) params.set('dueState', filters.dueState);
  if (filters.sort !== 'due') params.set('sort', filters.sort);
  if (filters.page > 1) params.set('page', String(filters.page));
  return `${root}${params.size ? `?${params}` : ''}`;
}

export function privateTaskSearch(state: unknown): string {
  const value = state && typeof state === 'object' && 'taskSearch' in state ? state.taskSearch : undefined;
  return typeof value === 'string' ? value.slice(0, taskSearchLimit) : '';
}

export function taskReturnContext(state: unknown, building?: string): TaskReturnContext | undefined {
  if (!state || typeof state !== 'object' || !('taskReturn' in state)) return undefined;
  const value = state.taskReturn;
  if (!value || typeof value !== 'object' || !('to' in value) || typeof value.to !== 'string') return undefined;
  if ((!value.to.startsWith('/work')&&!value.to.startsWith('/my-work')) || value.to.startsWith('//')) return undefined;
  const url = new URL(value.to, 'https://staff.invalid');
  if (url.origin !== 'https://staff.invalid' || !['/work','/my-work'].includes(url.pathname) || url.hash) return undefined;
  if ([...url.searchParams.keys()].some(key => !['building', 'source', 'dueState', 'sort', 'page'].includes(key))) return undefined;
  if ([...new Set(url.searchParams.keys())].some(key => url.searchParams.getAll(key).length !== 1)) return undefined;
  if ((url.searchParams.get('building') ?? undefined) !== building) return undefined;
  const filters = readTaskFilters(url.search);
  return { to: taskQueueHref(filters, building,url.pathname as '/work'|'/my-work'),
    search: 'search' in value && typeof value.search === 'string' ? value.search.slice(0, taskSearchLimit) : '',
    focusKey: 'focusKey' in value && typeof value.focusKey === 'string' ? value.focusKey.slice(0, 200) : '' };
}

export function taskRowElementId(key: string) { return `task-${encodeURIComponent(key)}`; }
